/**
 * The marketing site's navigation links (`/links`, contract §5), organised in the buckets
 * the page renders: `header`, and one bucket per footer group.
 *
 * `bucket(location, group)` is that bucket's links in display order. `move(link, ±1)`
 * swaps a link with its neighbour and sends the bucket's complete new order to
 * `PUT /links/reorder`, whose answer (orders rewritten 0..n-1) replaces the bucket.
 *
 * Writes REJECT with the axios error on failure (the caller owns the message —
 * `siteErrorMessage`). Every successful write calls `invalidateSiteContent()`. Create,
 * update and delete re-read the list: a link moved to another bucket changes two buckets'
 * orders, and the server is the one that knows them.
 *
 * @param {{ api?: typeof siteApi }} [options]
 * @returns {{
 *   status: "loading" | "ready" | "error", items: object[], error: unknown,
 *   reload: () => Promise<void>,
 *   bucket: (location: "header" | "footer", group?: string | null) => object[],
 *   create: (link: object) => Promise<void>,
 *   update: (linkId: string, link: object) => Promise<void>,
 *   remove: (linkId: string) => Promise<void>,
 *   move: (link: object, direction: -1 | 1) => Promise<void>,
 * }}
 */
import { useCallback, useEffect, useState } from "react";
import siteApi from "./siteApi";
import { invalidateSiteContent } from "../../lib/siteContentInvalidation";

const inBucket = (location, group) => (l) =>
  l.location === location && (location === "header" ? true : l.group === group);

const byOrder = (a, b) => a.order - b.order;

export default function useSiteLinks({ api = siteApi } = {}) {
  const [status, setStatus] = useState("loading");
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  const fetchItems = useCallback(async () => {
    const body = await api.listLinks();
    setItems(Array.isArray(body?.items) ? body.items : []);
  }, [api]);

  const reload = useCallback(async () => {
    setStatus("loading");
    try {
      await fetchItems();
      setError(null);
      setStatus("ready");
    } catch (e) {
      setError(e);
      setStatus("error");
    }
  }, [fetchItems]);

  useEffect(() => { reload(); }, [reload]);

  const bucket = useCallback(
    (location, group = null) => items.filter(inBucket(location, group)).sort(byOrder),
    [items],
  );

  const create = useCallback(async (link) => {
    await api.createLink(link);
    invalidateSiteContent();
    await fetchItems();
  }, [api, fetchItems]);

  const update = useCallback(async (linkId, link) => {
    await api.updateLink(linkId, link);
    invalidateSiteContent();
    await fetchItems();
  }, [api, fetchItems]);

  const remove = useCallback(async (linkId) => {
    await api.deleteLink(linkId);
    invalidateSiteContent();
    await fetchItems();
  }, [api, fetchItems]);

  const move = useCallback(async (link, direction) => {
    const group = link.location === "header" ? null : link.group;
    const ordered = items.filter(inBucket(link.location, group)).sort(byOrder);
    const from = ordered.findIndex((l) => l.link_id === link.link_id);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= ordered.length) return;
    const ids = ordered.map((l) => l.link_id);
    [ids[from], ids[to]] = [ids[to], ids[from]];
    const body = await api.reorderLinks({ location: link.location, group, linkIds: ids });
    const fresh = Array.isArray(body?.items) ? body.items : [];
    setItems((prev) => [...prev.filter((l) => !inBucket(link.location, group)(l)), ...fresh]);
    invalidateSiteContent();
  }, [api, items]);

  return { status, items, error, reload, bucket, create, update, remove, move };
}
